import React, { useEffect, useRef } from "react";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import enableGlobalData from "../../data/enableGlobal.json";
import safeGuardGlobalData from "../../data/safeguardGlobal.json";
import intermediateEcom from "../../data/intermediateEcom.json";
import intermediatePublic from "../../data/intermediatePublic.json";
import intermediatePersonal from "../../data/intermediatePersonal.json";
import ColorLegend from "../../../../components/ColorLegend/colorLegend";
import { select } from "d3-selection";
import { scaleLinear, scaleSqrt, scaleOrdinal } from "d3-scale";
import { forceSimulation, forceX, forceY, forceCollide } from "d3-force";
import { axisTop } from "d3-axis";
import { Text } from "@visx/text";

const Beeswarm = ({
  data,
  title,
  note,
  source,
  less,
  more,
  lowIncome,
  lowerMiddleIncome,
  upperMiddleIncome,
  highIncome,
}) => {
  const { ref, width, height, windowSize, inView } = useVizualisation();

  const margin = {
    top: width < 770 ? 35 : 15,
    right: 50,
    bottom: 0,
    left: 50,
  };

  const beeRadius = width < 770 ? 7 : 9;

  const xAxisRef = useRef(null);

  const chartWidth = width - margin.left - margin.right;
  const chartHeight = 170 - margin.top - margin.bottom;

  const datamap = {
    safeguard: safeGuardGlobalData,
    enable: enableGlobalData,
    ecommerce: intermediateEcom,
    public: intermediatePublic,
    personal: intermediatePersonal,
  };

  let beeswarmData = datamap[data];

  const xScale = scaleLinear().domain([0, 100]).range([0, chartWidth]);
  const xAxis = axisTop(xScale)
    .tickSize(-chartHeight)
    .tickPadding(0)
    .tickValues([0, 25, 50, 75, 100]);

  const incomeGroups = [...new Set(beeswarmData.map((d) => d.income))];
  const translationsMap = {
    "Low-income": lowIncome,
    "Lower-middle-income": lowerMiddleIncome,
    "Upper-middle-income": upperMiddleIncome,
    "High-income": highIncome,
  };

  const colorScale = scaleOrdinal()
    .domain(incomeGroups)
    .range(["#481F7C", "#B6A3CA", "#FCC0B4", "#EE6047"].reverse());
  const colorScaleTranslated = scaleOrdinal()
    .domain(incomeGroups.map((d) => translationsMap[d]))
    .range(["#481F7C", "#B6A3CA", "#FCC0B4", "#EE6047"].reverse());

  const backgroundColorScale = scaleLinear()
    .domain([0, 100])
    .range(["#E5E5E5", "#B0CBCD"]);

  const sizeScale = scaleSqrt().domain([0, 1400000000]).range([6, 50]);

  const applyForce = (nodes) => {
    const simulation = forceSimulation(nodes)
      .force("x", forceX((d) => xScale(d.value)).strength(0.1))
      .force(
        "y",
        forceY((d) => chartHeight / 2)
      )
      .force("collide", forceCollide(beeRadius).strength(0.8))
      .stop();

    let i = 0;
    while (simulation.alpha() > 0.01 && i < 500) {
      simulation.tick();
      i++;
    }
  };

  applyForce(beeswarmData);

  useEffect(() => {
    select(xAxisRef.current).call(xAxis);
  });

  return (
    <>
      <ColorLegend colorscale={colorScaleTranslated}></ColorLegend>
      <Vizualisation
        title={title}
        note={note}
        source={source}
        ref={ref}
        size="xLarge"
      >
        <svg width={width} height={170}>
          <defs>
            <marker
              id="arrow"
              viewBox="0 -10 20 20"
              refX={5}
              refY={0}
              markerWidth={8}
              markerHeight={8}
              orient="auto"
            >
              <path d="M0,-10L20,0L0,10" className="arrowhead"></path>
            </marker>
          </defs>
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            <g className="g-axis">
              <g
                className="axis axis--x"
                transform={`translate(0,0)`}
                ref={xAxisRef}
              ></g>
            </g>
            <g className="background-rects">
              {xAxis
                .tickValues()
                .slice(0, -1)
                .map((d) => {
                  return (
                    <rect
                      key={"rect-" + d}
                      x={xScale(d)}
                      y={12}
                      width={xScale(25)}
                      height={chartHeight}
                      fill={backgroundColorScale(d)}
                      opacity={0.6}
                    ></rect>
                  );
                })}
            </g>
            <g className="g-beeswarm">
              <g className="g-circles">
                {beeswarmData.map((d, i) => {
                  return (
                    <>
                      <circle
                        key={`circle-${data + "-" + d.country}`}
                        cx={d.x}
                        cy={d.y}
                        r={beeRadius}
                        fill={colorScale(d.income)}
                        stroke={"#ffffff"}
                        strokeWidth={1}
                      ></circle>
                    </>
                  );
                })}
              </g>
            </g>
            <g
              className="arrow-annotations"
              transform={`translate(0, ${width < 770 ? -20 : 0})`}
            >
              <line
                x1={xScale(48)}
                x2={xScale(48) - 120}
                y1={0}
                y2={0}
                stroke={"#000000"}
                strokeWidth={1}
                markerEnd={"url(#arrow)"}
              ></line>
              <Text
                x={xScale(48)}
                y={0}
                textAnchor={"end"}
                verticalAnchor={"end"}
                fontSize={"11px"}
                dy={-3}
              >
                {less}
              </Text>
              <line
                x1={xScale(52)}
                x2={xScale(52) + 120}
                y1={0}
                y2={0}
                stroke={"#000000"}
                strokeWidth={1}
                markerEnd={"url(#arrow)"}
              ></line>
              <Text
                x={xScale(52)}
                y={0}
                textAnchor={"start"}
                verticalAnchor={"end"}
                fontSize={"11px"}
                dy={-3}
              >
                {more}
              </Text>
            </g>
          </g>
        </svg>
      </Vizualisation>
    </>
  );
};

export default Beeswarm;
